// frontend/src/components/search/SearchBar.tsx

import React, { useState, useEffect, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useSearch } from '@/hooks/useSearch'
import { Input } from '@/components/common/UI/Input'
import { Button } from '@/components/common/UI/Button'
import { cn } from '@/utils/helpers'

interface SearchBarProps {
  placeholder?: string
  onSearch?: (query: string) => void
  initialValue?: string
  className?: string
}

export const SearchBar: React.FC<SearchBarProps> = ({
  placeholder = 'Поиск...',
  onSearch,
  initialValue = '',
  className
}) => {
  const navigate = useNavigate()
  const [query, setQuery] = useState(initialValue)
  const [isFocused, setIsFocused] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const { getAutocomplete } = useSearch()

  const { data: suggestions } = getAutocomplete(query)

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (query.trim()) {
      if (onSearch) {
        onSearch(query.trim())
      } else {
        navigate(`/vacancies?q=${encodeURIComponent(query.trim())}`)
      }
      setIsFocused(false)
    }
  }

  const handleSuggestionClick = (suggestion: string) => {
    setQuery(suggestion)
    if (onSearch) {
      onSearch(suggestion)
    } else {
      navigate(`/vacancies?q=${encodeURIComponent(suggestion)}`)
    }
    setIsFocused(false)
  }

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (inputRef.current && !inputRef.current.contains(e.target as Node)) {
        setIsFocused(false)
      }
    }
    document.addEventListener('click', handleClickOutside)
    return () => document.removeEventListener('click', handleClickOutside)
  }, [])

  return (
    <div className={cn('relative w-full', className)} ref={inputRef}>
      <form onSubmit={handleSubmit} className="relative">
        <Input
          type="text"
          placeholder={placeholder}
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          className="pr-12"
          leftIcon={
            <svg className="w-5 h-5 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          }
        />
        <Button
          type="submit"
          size="sm"
          className="absolute right-1 top-1/2 -translate-y-1/2"
        >
          Найти
        </Button>
      </form>

      {/* Автодополнение */}
      {isFocused && query.length >= 2 && suggestions && suggestions.length > 0 && (
        <div className="absolute top-full left-0 right-0 mt-1 bg-white rounded-lg shadow-lg border border-secondary-200 overflow-hidden z-50">
          {suggestions.map((suggestion: any, index: number) => (
            <button
              key={index}
              className="w-full px-4 py-2 text-left hover:bg-secondary-50 transition-colors flex items-center gap-2"
              onClick={() => handleSuggestionClick(suggestion.text)}
            >
              <svg className="w-4 h-4 text-secondary-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
              <span className="text-sm">{suggestion.text}</span>
              {suggestion.type && (
                <span className="ml-auto text-xs text-secondary-400">
                  {suggestion.type === 'vacancy' ? 'Вакансия' : 
                   suggestion.type === 'company' ? 'Компания' : 'Навык'}
                </span>
              )}
            </button>
          ))}
        </div>
      )}
    </div>
  )
}